import React, {useEffect, useMemo, useState} from 'react';
import {Modal} from './Modal';

export type EntityPickerModalProps<T> = {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (id: string) => void;
    title: string;
    description?: string;
    confirmLabel?: string;
    loadOptions: () => Promise<T[]>;
    getKey: (item: T) => string;
    getPrimary: (item: T) => React.ReactNode;
    getSecondary?: (item: T) => React.ReactNode;
    filter?: (item: T, q: string) => boolean;
    placeholder?: string;
};

export function EntityPickerModal<T>({
                                         isOpen,
                                         onClose,
                                         onConfirm,
                                         title,
                                         description,
                                         confirmLabel = 'Confirm',
                                         loadOptions,
                                         getKey,
                                         getPrimary,
                                         getSecondary,
                                         filter,
                                         placeholder = 'Search...'
                                     }: EntityPickerModalProps<T>) {
    const [items, setItems] = useState<T[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedId, setSelectedId] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setSearch('');
            setSelectedId('');
            setError(null);
            return;
        }
        (async () => {
            try {
                setLoading(true);
                const data = await loadOptions();
                setItems(data);
            } catch (e) {
                console.error(e);
                setError('Failed to load data');
            } finally {
                setLoading(false);
            }
        })();
    }, [isOpen, loadOptions]);

    const filtered = useMemo(() => {
        const q = search;
        if (!q) return items;
        return filter ? items.filter(i => filter(i, q)) : items;
    }, [items, search, filter]);

    const handleConfirm = () => {
        if (!selectedId) {
            setError('Please select an item');
            return;
        }
        onConfirm(selectedId);
        setSelectedId('');
        setSearch('');
    };

    const handleClose = () => {
        setSelectedId('');
        setSearch('');
        setError(null);
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={title}
            size="lg"
            footer={(
                <div className="flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={!selectedId}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                        {confirmLabel}
                    </button>
                </div>
            )}
        >
            {description && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    {description}
                </p>
            )}

            <input
                type="text"
                placeholder={placeholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />

            <div
                className="max-h-80 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                    <div className="p-6 text-center text-gray-500 dark:text-gray-400">Loading...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-6 text-center text-gray-500 dark:text-gray-400">No items found</div>
                ) : (
                    filtered.map(item => {
                        const id = getKey(item);
                        const active = selectedId === id;
                        return (
                            <button
                                key={id}
                                type="button"
                                onClick={() => {
                                    setSelectedId(id);
                                    setError(null);
                                }}
                                className={`w-full text-left p-4 transition-colors ${active ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div>{getPrimary(item)}</div>
                                    {getSecondary && (
                                        <div
                                            className="text-xs text-gray-500 dark:text-gray-400 text-right max-w-[240px]">
                                            {getSecondary(item)}
                                        </div>
                                    )}
                                </div>
                            </button>
                        );
                    })
                )}
            </div>

            {error && (
                <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
            )}
        </Modal>
    );
}

export default EntityPickerModal;
